import { Minus, Plus } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import type { CartLine } from './useOrderState';

interface Props {
  lines: CartLine[];
  onSetQty: (id: number, qty: number) => void;
  compact?: boolean;
}

export function CartLines({ lines, onSetQty, compact = false }: Props) {
  if (lines.length === 0) {
    return (
      <p className="text-[0.8125rem] text-(--color-text-secondary) text-center py-4">
        <Trans>No items yet.</Trans>
      </p>
    );
  }

  if (compact) {
    return (
      <ul className="space-y-0.5">
        {lines.map((line) => (
          <li
            key={line.menuItem.id}
            className="flex items-center gap-2 text-[0.8125rem] px-2 py-1.5 rounded-md hover:bg-(--color-surface-muted)/50"
          >
            <span className="w-5 text-center font-bold text-(--color-text-primary)">{line.qty}×</span>
            <span className="flex-1 truncate">{line.menuItem.name}</span>
            <span className="text-(--color-text-secondary)">
              {line.menuItem.price != null
                ? `$${(line.menuItem.price * line.qty).toFixed(2)}`
                : '—'}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="space-y-2">
      {lines.map((line) => (
        <li
          key={line.menuItem.id}
          className="flex items-center gap-2.5 px-2 py-2 rounded-xl bg-(--color-surface-content) border border-(--color-border)"
        >
          {/* Thumb */}
          <div className="w-[2.625rem] h-[2.625rem] rounded-lg bg-(--color-surface-muted) overflow-hidden shrink-0">
            {line.menuItem.imageUrl && (
              <img
                src={line.menuItem.imageUrl}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </div>

          {/* Name + meta */}
          <div className="flex-1 min-w-0">
            <div className="text-[0.84375rem] font-semibold text-(--color-text-primary) truncate">
              {line.menuItem.name}
            </div>
            <div className="text-[0.75rem] text-(--color-text-secondary)">
              {line.menuItem.station && `${line.menuItem.station} · `}
              {line.menuItem.price != null ? `$${line.menuItem.price.toFixed(2)}` : '—'}
            </div>
          </div>

          {/* Qty pill */}
          <div className="flex items-center gap-0.5 bg-(--color-surface-muted) border border-(--color-border) rounded-full p-0.5">
            <button
              type="button"
              onClick={() => onSetQty(line.menuItem.id, line.qty - 1)}
              className="w-6 h-6 rounded-full flex items-center justify-center text-(--color-text-secondary) hover:bg-(--color-surface-content) hover:text-(--color-text-primary) transition-colors"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="min-w-[1.125rem] text-center font-bold text-[0.8125rem] text-(--color-text-primary)">
              {line.qty}
            </span>
            <button
              type="button"
              onClick={() => onSetQty(line.menuItem.id, line.qty + 1)}
              className="w-6 h-6 rounded-full flex items-center justify-center text-(--color-text-secondary) hover:bg-(--color-surface-content) hover:text-(--color-text-primary) transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
